import { buildReadingPlanDaysForDate, isPlanListable, type ReadingPlanDay } from '@soulhx/fs-common';
import { db, type LocalInstantiatedPlan, type LocalInstantiatedPlanDay, type LocalPlan } from '../model/db.js';
import { useAsyncLiveQuery } from './useAsyncLiveQuery.js';
import { localToday } from '../sync/timestamps.js';

/*
 * Live-reactive reads of synced reading plan data. Everything derived
 * (progress, plan status, the readings for a date) is computed here from
 * the synced rows, using the shared fs-common helpers, so it's correct
 * offline and immediately after a local change.
 */

/**
 * Plans for the list of available plans (i.e. not deleted/superseded
 * plans, which are only kept for subscribers), most recently updated
 * first
 */
export const useListablePlans = () =>
  useAsyncLiveQuery(
    async () => (await db.plans.orderBy('updateDate').reverse().toArray()).filter(isPlanListable),
    [],
    [] as LocalPlan[]
  );

/**
 * A single plan (version), or `undefined` if it isn't (or isn't yet)
 * synced. `id` may be `undefined` so this can always be called.
 */
export const usePlanById = (id: string | undefined): { data: LocalPlan | undefined; isLoading: boolean } => {
  // dexie-react-hooks' useLiveQuery keeps returning the *previous* result,
  // marked as resolved, until the query for a new `id` catches up - e.g. an
  // `undefined` from before navigating to a just-saved plan, which would
  // look like "not found". So each result is stamped with the ID it's for,
  // and only counts as loaded once that matches.
  const { data } = useAsyncLiveQuery(
    async () => ({ forId: id, plan: id === undefined ? undefined : await db.plans.get(id) }),
    [id]
  );

  const isCurrent = !!data && data.forId === id;
  return { data: isCurrent ? data.plan : undefined, isLoading: !isCurrent };
};

export interface ActiveInstantiatedPlan {
  ip: LocalInstantiatedPlan;

  /**
   * Can be missing briefly, if the subscription synced before its plan
   */
  plan?: LocalPlan;

  /**
   * Sorted by scheduled date
   */
  days: LocalInstantiatedPlanDay[];
}

const getActiveInstantiatedPlans = async (): Promise<ActiveInstantiatedPlan[]> => {
  const ips = (await db.instantiatedPlans.toArray()).filter((ip) => ip.syncStatus !== 'pendingDelete');
  if (ips.length === 0) return [];

  // one `equals` query per subscription rather than a single `anyOf`: Dexie
  // has to walk an IDB cursor (a request per row) for `anyOf`, while a
  // plain key range is read in bulk with `getAll()` - for a few thousand
  // days that's seconds vs. milliseconds. Sorted here rather than with
  // `sortBy`, which would also force a cursor.
  const [plans, daysByIp] = await Promise.all([
    db.plans.bulkGet(ips.map((ip) => ip.planInstanceId)),
    Promise.all(ips.map((ip) => db.instantiatedPlanDays.where('instantiatedPlanId').equals(ip.id).toArray())),
  ]);

  return ips.map((ip, i) => ({
    ip,
    plan: plans[i],
    days: daysByIp[i].sort((a, b) => a.scheduledDate.localeCompare(b.scheduledDate)),
  }));
};

/**
 * The user's subscriptions (excluding any being unsubscribed), each with
 * its plan and days
 */
export const useActiveInstantiatedPlans = () =>
  useAsyncLiveQuery(getActiveInstantiatedPlans, [], [] as ActiveInstantiatedPlan[]);

/**
 * The readings scheduled for a date, built from already-loaded
 * subscriptions - for components that also need the subscriptions
 * themselves, so they can make one DB read rather than two (and not
 * re-read when the date changes)
 *
 * @param active The user's subscriptions (from `useActiveInstantiatedPlans`)
 * @param date The date being viewed (`yyyy-MM-dd`)
 */
export const getReadingPlanDaysForDate = (active: ActiveInstantiatedPlan[], date: string): ReadingPlanDay[] =>
  buildReadingPlanDaysForDate(
    date,
    localToday(),
    active.flatMap((a) => (a.plan ? [a.plan] : [])),
    active.map((a) => a.ip),
    active.flatMap((a) => a.days)
  );

/**
 * The readings scheduled for a date, across all of the user's
 * subscriptions (replaces the API's "reading plan for day" endpoint)
 *
 * @param date The date being viewed (`yyyy-MM-dd`)
 */
export const useReadingPlanForDate = (date: string) =>
  useAsyncLiveQuery(
    async (): Promise<ReadingPlanDay[]> => getReadingPlanDaysForDate(await getActiveInstantiatedPlans(), date),
    [date],
    [] as ReadingPlanDay[]
  );
